import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LayoutGrid,
  Users,
  Drama,
  Plus,
  Music,
  History,
  Upload,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import InfoPopover from '../components/common/InfoPopover';
import { cn } from '@/lib/utils';
import { TalentKategori, Talent } from '../services/api';
import { useTalentData } from '../hooks/useTalentData';
import TalentTree from '../components/settings/TalentTree';
import TalentDialog from '../components/settings/TalentDialog';
import TalentImportDialog from '../components/settings/TalentImportDialog';
import UserManagement from '../components/settings/UserManagement';
import ConfirmDeleteTalentDialog from '../components/settings/ConfirmDeleteTalentDialog';
import ProduksjonsKategoriMal from '../components/settings/ProduksjonsKategoriMal';
import MusikkbibliotekList from '../components/settings/MusikkbibliotekList';
import AdminAuditLogg from '../components/settings/AdminAuditLogg';
import AppHeader from '../components/layout/AppHeader';

const SETTINGS_TABS = ['talent', 'produksjon', 'brukere', 'musikk', 'audit'] as const;
type SettingsTab = typeof SETTINGS_TABS[number];

interface TabConfig {
  id: SettingsTab;
  icon: LucideIcon;
  title: string;
  description: string;
}

const TAB_CONFIG: TabConfig[] = [
  {
    id: 'talent',
    icon: LayoutGrid,
    title: 'Talentkart',
    description: 'Administrer talenter og deres hierarkiske kategorier',
  },
  {
    id: 'produksjon',
    icon: Drama,
    title: 'Produksjonsmaler',
    description: 'Administrere produksjonsmaler og oppmøtetider',
  },
  {
    id: 'brukere',
    icon: Users,
    title: 'Brukere',
    description: 'Administrer brukere og deres roller',
  },
  {
    id: 'musikk',
    icon: Music,
    title: 'Sangbibliotek',
    description: 'Administrer sanger og sangkategorier',
  },
  {
    id: 'audit',
    icon: History,
    title: 'Audit-logg',
    description: 'Se administrative endringer i organisasjonen',
  },
];

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    kategorier,
    talenter,
    loading,
    error,
    setError,
    createKategori,
    updateKategori,
    deleteKategori,
    createTalent,
    updateTalent,
    deleteTalent,
    getChildren,
    getTalenterForKategori,
    kategoriSomKanHaTalenter,
    refresh,
  } = useTalentData();

  const [apneKategorier, setApneKategorier] = useState<Set<number>>(new Set());
  const [openDialog, setOpenDialog] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'kategori' | 'talent'>('kategori');
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedItem, setSelectedItem] = useState<TalentKategori | Talent | null>(null);
  const [initialParentId, setInitialParentId] = useState<number | undefined>();
  const [initialKategoriId, setInitialKategoriId] = useState<number | undefined>();

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'talent' | 'kategori'>('talent');
  const [itemToDelete, setItemToDelete] = useState<{ id: number; navn: string; kategoriNavn?: string; childCount?: number } | null>(null);

  // Les activeTab fra URL, default til 'talent'
  const getInitialTab = (): SettingsTab => {
    const tabFromUrl = searchParams.get('tab');
    if (SETTINGS_TABS.includes(tabFromUrl as SettingsTab)) {
      return tabFromUrl as SettingsTab;
    }
    return 'talent';
  };

  const [activeTab, setActiveTab] = useState<SettingsTab>(getInitialTab);

  // Oppdater URL når tab endres
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Synkroniser med URL ved endringer (f.eks. browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      if (SETTINGS_TABS.includes(tabFromUrl as SettingsTab)) setActiveTab(tabFromUrl as SettingsTab);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleKategori = (kategoriId: number) => {
    setApneKategorier((prev) => {
      const nySet = new Set(prev);
      if (nySet.has(kategoriId)) {
        nySet.delete(kategoriId);
      } else {
        nySet.add(kategoriId);
      }
      return nySet;
    });
  };

  const openCreateKategoriDialog = (parentId?: number) => {
    setDialogType('kategori');
    setDialogMode('create');
    setSelectedItem(null);
    setInitialParentId(parentId);
    setOpenDialog(true);
  };

  const openEditKategoriDialog = (kategori: TalentKategori) => {
    setDialogType('kategori');
    setDialogMode('edit');
    setSelectedItem(kategori);
    setOpenDialog(true);
  };

  const openCreateTalentDialog = (kategoriId: number) => {
    setDialogType('talent');
    setDialogMode('create');
    setSelectedItem(null);
    setInitialKategoriId(kategoriId);
    setOpenDialog(true);
  };

  const openEditTalentDialog = (talent: Talent) => {
    setDialogType('talent');
    setDialogMode('edit');
    setSelectedItem(talent);
    setOpenDialog(true);
  };

  const handleSave = async (data: any) => {
    try {
      if (dialogType === 'kategori') {
        if (dialogMode === 'create') {
          await createKategori(data);
        } else if (selectedItem) {
          await updateKategori(selectedItem.id, data);
        }
        return;
      }

      if (dialogMode === 'create') {
        await createTalent(data);
      } else if (selectedItem) {
        await updateTalent(selectedItem.id, data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke lagre. Vennligst prøv igjen.');
      throw err;
    }
  };

  const handleDeleteKategoriClick = (id: number, navn: string) => {
    // Tell antall children (sub-kategorier + talents i denne kategorien)
    const children = getChildren(id);
    const talents = getTalenterForKategori(id);
    const childCount = children.length + talents.length;

    setDeleteType('kategori');
    setItemToDelete({ id, navn, childCount });
    setDeleteDialogOpen(true);
  };

  const handleDeleteTalentClick = (id: number, navn: string, kategoriNavn?: string) => {
    setDeleteType('talent');
    setItemToDelete({ id, navn, kategoriNavn });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      setError(null);
      setDeleteDialogOpen(false);

      if (deleteType === 'kategori') {
        await deleteKategori(itemToDelete.id);
      } else {
        await deleteTalent(itemToDelete.id);
      }

      setItemToDelete(null);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Kunne ikke slette. Vennligst prøv igjen.';
      setError(errorMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <AppHeader />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-8">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-sm underline">
                Lukk
              </button>
            </AlertDescription>
          </Alert>
        )}

        {/* Oversikt Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {TAB_CONFIG.map(({ id, icon: Icon, title, description }) => {
            const isActive = activeTab === id;
            return (
              <Card
                key={id}
                className={cn(
                  'shadow-md cursor-pointer transition-shadow hover:shadow-lg',
                  isActive && 'border-2 border-primary'
                )}
                onClick={() => setActiveTab(id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={cn('h-5 w-5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                    <h3 className="text-lg font-semibold">{title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </CardContent>
                <CardFooter className="px-6 pb-4 pt-0">
                  <Button variant="ghost" size="sm" disabled={isActive}>
                    {isActive ? 'Aktiv' : 'Vis'}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Talenthierarki */}
        {activeTab === 'talent' && (
          <Card className="p-6 shadow-md">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-semibold">Talentkart</h2>
                <InfoPopover label="Hva er talentkartet?">
                  <p>
                    Talentkartet er den totale samlingen av alle talenter som det er behov
                    for i ulike typer produksjoner. Eksempel: Tenor, Rigger,
                    Kjøkkentjeneste, Lydmikser.
                  </p>
                  <p>Talenter kan grupperes for bedre oversikt.</p>
                  <p>Du kan også importere kategorier og talenter fra en Excel- eller CSV-fil.</p>
                </InfoPopover>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Importer
                </Button>
                <Button onClick={() => openCreateKategoriDialog()}>
                  <Plus className="mr-2 h-4 w-4" />
                  Ny rot-kategori
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <TalentTree
                kategorier={kategorier}
                talenter={talenter}
                apneKategorier={apneKategorier}
                onToggle={toggleKategori}
                onEditKategori={openEditKategoriDialog}
                onDeleteKategori={handleDeleteKategoriClick}
                onCreateSubKategori={openCreateKategoriDialog}
                onCreateTalent={openCreateTalentDialog}
                onEditTalent={openEditTalentDialog}
                onDeleteTalent={handleDeleteTalentClick}
                getChildren={getChildren}
                getTalenterForKategori={getTalenterForKategori}
              />
            )}
          </Card>
        )}

        {/* Produksjonsmaler */}
        {activeTab === 'produksjon' && (
          <Card className="p-6 shadow-md">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold">Produksjonsmaler</h2>
              <InfoPopover label="Hva er produksjonsmaler?">
                <p>
                  En mal kan bestå av Kjøreplan, Bemanningsplan og Oppmøtetider. Lag maler for
                  produksjoner som ofte er like for å spare tid.
                </p>
                <p>Du kan velge å bruke en mal når du oppretter en produksjon.</p>
              </InfoPopover>
            </div>
            <ProduksjonsKategoriMal />
          </Card>
        )}

        {/* Bruker Administrasjon */}
        {activeTab === 'brukere' && (
          <Card className="p-6 shadow-md">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold">Brukeradministrasjon</h2>
              <InfoPopover label="Om brukerlisten">
                <p>
                  Listen viser alle registrerte brukere for denne organisasjonen. Klikk inn på
                  en bruker for å vise informasjonen som er registrert. Har du rettigheter, kan
                  du også endre flere felter.
                </p>
              </InfoPopover>
            </div>
            <UserManagement />
          </Card>
        )}

        {/* Sangbibliotek */}
        {activeTab === 'musikk' && (
          <Card className="p-6 shadow-md">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl font-semibold">Sangbibliotek</h2>
              <InfoPopover label="Hva er sangbiblioteket?">
                <p>
                  Biblioteket lister alle registrerte sanger i organisasjonen. Sanger kan være
                  publiserte og ikke-publiserte. Ikke-publiserte sanger kan ikke lenkes til i en
                  produksjon.
                </p>
              </InfoPopover>
            </div>
            <MusikkbibliotekList />
          </Card>
        )}

        {/* Admin audit-logg */}
        {activeTab === 'audit' && (
          <Card className="p-6 shadow-md">
            <AdminAuditLogg kilde="org" />
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <TalentDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleSave}
        type={dialogType}
        mode={dialogMode}
        item={selectedItem}
        kategorier={kategorier}
        kategoriSomKanHaTalenter={kategoriSomKanHaTalenter}
        initialParentId={initialParentId}
        initialKategoriId={initialKategoriId}
      />

      <TalentImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportert={refresh}
      />

      {/* Delete Confirmation Dialog */}
      {itemToDelete && (
        <ConfirmDeleteTalentDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          type={deleteType}
          itemName={itemToDelete.navn}
          kategoriNavn={itemToDelete.kategoriNavn}
          childCount={itemToDelete.childCount}
        />
      )}
    </div>
  );
};

export default Settings;
